(() => {
  const COLOR_WORDS = /farbe|color|colour/i;
  const MOBILE_GALLERY = '(max-width: 989px)';

  function normalizeColor(value) {
    const v = String(value || '').trim().toLowerCase();
    if (v.includes('cream') || v.includes('creme')) return 'cream';
    if (v.includes('beige')) return 'beige';
    if (v.includes('grey') || v.includes('gray') || v.includes('grau')) return 'grey';
    return null;
  }

  function mediaColor(node) {
    const img = node?.querySelector?.('img');
    const text = [
      img?.alt,
      img?.currentSrc,
      img?.src,
      node?.getAttribute?.('data-media-color'),
      node?.textContent,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (text.includes('cream') || text.includes('creme')) return 'cream';
    if (text.includes('beige')) return 'beige';
    if (text.includes('grey') || text.includes('gray') || text.includes('grau')) return 'grey';
    return null;
  }

  function selectedColor(info) {
    const fields = [...info.querySelectorAll('fieldset.product-form__input, .product-form__input')];
    for (const field of fields) {
      const label = field.querySelector('legend, .form__label')?.textContent || '';
      if (!COLOR_WORDS.test(label)) continue;
      const checked = field.querySelector('input:checked');
      const select = field.querySelector('select');
      const value = checked?.value || select?.value || '';
      const color = normalizeColor(value);
      if (color) return color;
    }
    return null;
  }

  function visibleSlides(viewer) {
    const slider = viewer?.slider || viewer?.querySelector?.('[id^="Slider-"]');
    if (!slider) return [];
    return [...slider.querySelectorAll('.product__media-item[data-media-id]')].filter(
      (item) => !item.classList.contains('sultan-color-hidden') && item.clientWidth > 0
    );
  }

  function closestVisibleIndex(viewer, slides = visibleSlides(viewer)) {
    const slider = viewer?.slider || viewer?.querySelector?.('[id^="Slider-"]');
    if (!slider || !slides.length) return 0;

    let bestIndex = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    slides.forEach((slide, index) => {
      const distance = Math.abs(slider.scrollLeft - slide.offsetLeft);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });
    return bestIndex;
  }

  function syncViewer(viewer) {
    if (!viewer) return;
    const slider = viewer.slider || viewer.querySelector('[id^="Slider-"]');
    if (!slider) return;

    const slides = visibleSlides(viewer);
    if (!slides.length) return;

    const index = closestVisibleIndex(viewer, slides);
    const current = viewer.querySelector('.slider-counter--current');
    const total = viewer.querySelector('.slider-counter--total');
    const prev = viewer.querySelector('button[name="previous"]');
    const next = viewer.querySelector('button[name="next"]');

    if (current) current.textContent = String(index + 1);
    if (total) total.textContent = String(slides.length);
    if (prev) prev.toggleAttribute('disabled', index <= 0);
    if (next) next.toggleAttribute('disabled', index >= slides.length - 1);

    viewer.sliderItemsToShow = slides;
    viewer.totalPages = slides.length;
    viewer.slidesPerPage = 1;
    if (slides.length > 1) {
      const offset = slides[1].offsetLeft - slides[0].offsetLeft;
      viewer.sliderItemOffset = offset > 0 ? offset : slider.clientWidth;
    } else {
      viewer.sliderItemOffset = slider.clientWidth || 1;
    }
  }

  function moveVisibleSlide(viewer, direction) {
    const slider = viewer?.slider || viewer?.querySelector?.('[id^="Slider-"]');
    const slides = visibleSlides(viewer);
    if (!slider || !slides.length) return;

    const currentIndex = closestVisibleIndex(viewer, slides);
    const nextIndex = Math.max(0, Math.min(slides.length - 1, currentIndex + direction));
    const target = slides[nextIndex];

    slider.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    window.setTimeout(() => syncViewer(viewer), 80);
    window.setTimeout(() => syncViewer(viewer), 320);
  }

  function bindVisibleNavigation(viewer) {
    if (!viewer || viewer.dataset.sultanVisibleNavBound === 'true') return;
    viewer.dataset.sultanVisibleNavBound = 'true';

    const slider = viewer.slider || viewer.querySelector('[id^="Slider-"]');
    const prev = viewer.querySelector('button[name="previous"]');
    const next = viewer.querySelector('button[name="next"]');

    const intercept = (direction) => (event) => {
      if (!window.matchMedia(MOBILE_GALLERY).matches) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      moveVisibleSlide(viewer, direction);
    };

    prev?.addEventListener('click', intercept(-1), true);
    next?.addEventListener('click', intercept(1), true);

    let raf = 0;
    slider?.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => syncViewer(viewer));
      },
      { passive: true }
    );

    window.addEventListener('resize', () => window.setTimeout(() => syncViewer(viewer), 50));
  }

  function prepareViewer(gallery) {
    const viewer = gallery?.querySelector?.('[id^="GalleryViewer"]');
    if (!viewer) return;

    bindVisibleNavigation(viewer);

    if (typeof viewer.resetPages === 'function') viewer.resetPages();
    requestAnimationFrame(() => {
      syncViewer(viewer);
      requestAnimationFrame(() => syncViewer(viewer));
    });
    window.setTimeout(() => syncViewer(viewer), 180);
  }

  function applyColorFilter(info) {
    const color = selectedColor(info);
    if (!color) return;

    const section = info.closest('.shopify-section') || document;
    const gallery = section.querySelector('media-gallery') || document.querySelector('media-gallery');
    if (!gallery) return;

    const items = [...gallery.querySelectorAll('.product__media-list [data-media-id]')];
    if (!items.length) return;

    const colorsById = new Map();
    let recognized = 0;
    let previousColor = null;

    for (const item of items) {
      let c = mediaColor(item);
      if (!c && previousColor) c = previousColor;
      if (c) previousColor = c;
      colorsById.set(item.dataset.mediaId, c);
      if (c) recognized += 1;
    }
    if (!recognized) return;

    for (const item of items) {
      const show = colorsById.get(item.dataset.mediaId) === color;
      item.classList.toggle('sultan-color-hidden', !show);
      item.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    const first = items.find((item) => !item.classList.contains('sultan-color-hidden'));
    if (first) {
      items.forEach((item) => item.classList.remove('is-active'));
      first.classList.add('is-active');
      if (typeof gallery.setActiveMedia === 'function') {
        gallery.setActiveMedia(first.dataset.mediaId, false);
      }
    }

    gallery.querySelectorAll('[data-target]').forEach((thumb) => {
      const target = String(thumb.dataset.target || '').split('-').pop();
      const matchingId = [...colorsById.keys()].find((id) => String(id).endsWith(target));
      const c = matchingId ? colorsById.get(matchingId) : mediaColor(thumb);
      thumb.classList.toggle('sultan-color-hidden', c !== color);
    });

    const modal = section.querySelector('product-modal') || document.querySelector('product-modal');
    if (modal) {
      modal.querySelectorAll('[data-media-id]').forEach((media) => {
        const id = media.dataset.mediaId;
        const c = colorsById.get(id) || mediaColor(media);
        media.classList.toggle('sultan-color-hidden', c !== color);
      });
    }

    prepareViewer(gallery);
  }

  function applyAll() {
    document.querySelectorAll('product-info').forEach(applyColorFilter);
  }

  function installMobileGeometryFix() {
    if (document.getElementById('sultan-gallery-last-photo-fix')) return;
    const style = document.createElement('style');
    style.id = 'sultan-gallery-last-photo-fix';
    style.textContent = `
      @media screen and (max-width: 989px) {
        .product__media-list.slider--mobile {
          scroll-padding-left: 0 !important;
          scroll-padding-right: 0 !important;
        }
        .product__media-list.slider--mobile::after {
          content: none !important;
          display: none !important;
          width: 0 !important;
          padding: 0 !important;
          margin: 0 !important;
        }
        .product__media-list .product__media-item:not(.sultan-color-hidden) {
          margin-left: 0 !important;
          margin-right: 0 !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  installMobileGeometryFix();

  document.addEventListener('DOMContentLoaded', () => {
    installMobileGeometryFix();
    applyAll();
    setTimeout(applyAll, 250);
  });

  document.addEventListener(
    'change',
    (event) => {
      if (!event.target.closest('product-info')) return;
      setTimeout(applyAll, 80);
      setTimeout(applyAll, 260);
    },
    true
  );

  document.addEventListener('shopify:section:load', () => setTimeout(applyAll, 50));

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('modal-opener.product__modal-opener--image');
    if (!opener) return;
    const toggle = opener.querySelector('.product__media-toggle');
    const selector = opener.dataset.modal;
    if (!toggle || !selector) return;
    window.setTimeout(() => {
      const modal = document.querySelector(selector);
      if (modal && !modal.hasAttribute('open') && typeof modal.show === 'function') modal.show(toggle);
    }, 0);
  });
})();
